import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { cn } from '../../../shared';
import type { TokenDef } from '../services/interviewLinksApi';
import { filterTokens } from './filterTokens';
import {
  ALLOWED_HREF,
  applyLinkToRange,
  clearPending,
  enclosingLink,
  hasLinkTo,
  markPending,
  renderInto,
  replacePendingWithLink,
  serialize,
  textBeforeCaret,
  unwrapLink,
} from './linkEditorDom';

/**
 * `plaintext-only` keeps pasted and typed content plain; Firefox before 136 rejects it, and there
 * `true` plus the paste handler below does the same job. Probed once per page load.
 */
/** The ring on an existing link while its address is being edited. Never stored. */
const EDITING_CLASS = 'ring-2 ring-blue-400';

const EDIT_MODE: 'plaintext-only' | 'true' = (() => {
  if (typeof document === 'undefined') return 'true';
  const probe = document.createElement('div');
  try {
    probe.contentEditable = 'plaintext-only';
    return probe.contentEditable === 'plaintext-only' ? 'plaintext-only' : 'true';
  } catch {
    return 'true';
  }
})();

/**
 * The Email Library's body editor for candidate emails: select words, press 🔗 Link, paste the
 * URL, and the words turn into a blue link right in the text (Sofia, Oct 8 2026).
 *
 * PR #499 put a Link button above a plain textarea, which wrote `[link text](https://…)` into the
 * middle of the template — correct for the renderer, unreadable for the person editing it ("it
 * must be simpler"). A textarea cannot show a word in blue, so this is a contentEditable that
 * SHOWS links as anchors and STORES exactly what the textarea stored: the Markdown-ish text
 * `lib/mdEmail.js` renders on send. Nothing on the proxy or in saved templates changes, and
 * `**bold**`, `- ` bullets and `{tokens}` stay the plain text they always were.
 *
 * Uncontrolled on purpose. Re-rendering a contentEditable from props on every keystroke moves the
 * caret; so the DOM is rebuilt only when `value` changes from OUTSIDE (a template loads, a reset),
 * which is detected by comparing with the last value this editor emitted itself.
 *
 * `pair` is the same template in the other language. Templates carry DE and EN side by side and a
 * link added to one is usually wanted in both, so the URL row offers "Apply this for the English
 * version" — and warns on Apply when it is left unticked and the other version has no such link.
 * `linkRequest` is the other half: the sibling asks THIS editor to open its URL row prefilled,
 * when it could not find the same words here on its own.
 */
export function LinkTemplateEditor({
  value,
  onChange,
  tokens,
  label,
  id,
  rows = 4,
  placeholder,
  disabled,
  pair,
  linkRequest,
}: {
  value: string;
  onChange: (v: string) => void;
  tokens: TokenDef[];
  label?: string;
  id?: string;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  /** The same template in the other language. */
  pair?: { label: string; value: string; onApply: (href: string, words: string) => void };
  /** Sent by the sibling editor: open the URL row with this address. `nonce` makes it re-fire. */
  linkRequest?: { href: string; nonce: number } | null;
}) {
  const autoId = useId();
  const fieldId = id || `link-editor-${autoId}`;
  const labelId = `${fieldId}-label`;
  const root = useRef<HTMLDivElement>(null);
  const emitted = useRef<string | null>(null);
  const [menu, setMenu] = useState<{ query: string } | null>(null);
  const [hi, setHi] = useState(0);
  // The selection the Link button captured: a click on the URL field takes focus, and with it
  // the editor's selection, so it is kept here until Apply.
  const savedRange = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<HTMLAnchorElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  /** The highlighted words a new link is for, while the URL row is open. */
  const pending = useRef<HTMLSpanElement | null>(null);
  const [applyToPair, setApplyToPair] = useState(false);
  /** Opened by the other language: the author still has to pick the words here. */
  const [fromPair, setFromPair] = useState(false);

  useLayoutEffect(() => {
    const el = root.current;
    if (!el || value === emitted.current) return;
    renderInto(el, value);
    emitted.current = value;
  }, [value]);

  const matches = menu ? filterTokens(tokens, menu.query) : [];

  function emit() {
    const el = root.current;
    if (!el) return;
    const next = serialize(el);
    emitted.current = next;
    onChange(next);
  }

  /** The live caret, when it is inside this editor. */
  function caret(): { node: Node; offset: number; range: Range } | null {
    const el = root.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.endContainer)) return null;
    return { node: range.endContainer, offset: range.endOffset, range };
  }

  function placeCaretAfter(node: Node) {
    const sel = window.getSelection();
    if (!sel) return;
    const r = document.createRange();
    r.setStartAfter(node);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  }

  /*
    Edits the text node in place and puts the caret after the new text. The earlier version
    inserted a new node and called `normalize()`, which merges it into its neighbour and detaches
    it — and `placeCaretAfter` on a detached node throws. The throw skipped `emit()`, so the
    variable showed in the editor but never reached the saved template.
  */
  function replaceInTextNode(node: Text, start: number, end: number, text: string) {
    node.replaceData(start, end - start, text);
    const r = document.createRange();
    r.setStart(node, start + text.length);
    r.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }

  function onInput() {
    emit();
    const el = root.current;
    const c = caret();
    if (!el || !c) return setMenu(null);
    const m = /(^|\s)\/([\w-]*)$/.exec(textBeforeCaret(el, c.node, c.offset));
    setMenu(m ? { query: m[2] } : null);
    setHi(0);
  }

  /** Replace the "/query" just typed with `{name}`. */
  function insertToken(name: string) {
    const c = caret();
    if (!c || !menu || c.node.nodeType !== 3) return setMenu(null);
    const typed = menu.query.length + 1; // the "/" plus the query
    replaceInTextNode(c.node as Text, Math.max(0, c.offset - typed), c.offset, `{${name}}`);
    setMenu(null);
    emit();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!menu || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertToken(matches[hi].name);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMenu(null);
    }
  }

  /** Pasted text arrives as plain text: a pasted web page must not bring its markup along. */
  function onPaste(e: ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const c = caret();
    if (!text || !c) return;
    const { startContainer, startOffset, endContainer, endOffset } = c.range;
    if (startContainer === endContainer && startContainer.nodeType === 3) {
      replaceInTextNode(startContainer as Text, startOffset, endOffset, text);
    } else {
      c.range.deleteContents();
      const node = document.createTextNode(text);
      c.range.insertNode(node);
      placeCaretAfter(node);
    }
    emit();
  }

  /** Drop the highlight (or the ring on an existing link) without changing anything else. */
  function clearMarks() {
    if (pending.current?.isConnected) clearPending(pending.current);
    pending.current = null;
    root.current?.querySelectorAll('a[data-editing]').forEach((a) => {
      a.removeAttribute('data-editing');
      a.classList.remove(...EDITING_CLASS.split(' '));
    });
  }

  /**
   * Open the URL row for the author's selection, and keep those words highlighted while they type
   * the address. Inside an existing link: edit that link instead (ringed, with Remove).
   */
  function openLink() {
    const el = root.current;
    if (!el) return;
    clearMarks();
    const c = caret();
    let range: Range;
    if (c) {
      range = c.range.cloneRange();
    } else {
      // Nothing focused in the editor: put the new link at the end.
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }
    const existing = enclosingLink(range.commonAncestorContainer, el);
    if (existing) {
      existing.setAttribute('data-editing', '');
      existing.classList.add(...EDITING_CLASS.split(' '));
      savedRange.current = range;
    } else if (!range.collapsed) {
      pending.current = markPending(range, el);
      savedRange.current = null;
    } else {
      savedRange.current = range;
    }
    setEditingLink(existing);
    setLinkUrl(existing?.getAttribute('data-href') || '');
    setFromPair(false);
    setMenu(null);
    setLinkOpen(true);
  }

  // The other language asked for this link: open the row prefilled and say what is missing.
  // State follows the request during render (React's "adjust state on a prop change" pattern);
  // the DOM side — dropping marks, scrolling the field into view — runs in the effect below.
  const [seenRequest, setSeenRequest] = useState<number | null>(null);
  if (linkRequest && linkRequest.nonce !== seenRequest) {
    setSeenRequest(linkRequest.nonce);
    setEditingLink(null);
    setLinkUrl(linkRequest.href);
    setApplyToPair(false);
    setFromPair(true);
    setLinkOpen(true);
  }
  useEffect(() => {
    if (!linkRequest) return;
    clearMarks();
    savedRange.current = null;
    root.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fire on a new request only
  }, [linkRequest?.nonce]);

  function closeLink() {
    clearMarks();
    setLinkOpen(false);
    setEditingLink(null);
    setLinkUrl('');
    setFromPair(false);
    savedRange.current = null;
  }

  function applyLink() {
    const el = root.current;
    if (!el) return;
    const href = linkUrl.trim();
    if (!ALLOWED_HREF.test(href)) {
      showToast({ status: 'warning', description: 'Enter a full URL starting with https:// or mailto:' });
      return;
    }
    let a: HTMLAnchorElement;
    if (pending.current?.isConnected) {
      a = replacePendingWithLink(pending.current, href, el);
      pending.current = null;
    } else if (savedRange.current) {
      a = applyLinkToRange(savedRange.current, href, el);
    } else {
      // Opened from the other language: use what the author has selected here since.
      const c = caret();
      if (!c || c.range.collapsed) {
        showToast({ status: 'warning', description: 'Select the words for this link in the text first.' });
        return;
      }
      a = applyLinkToRange(c.range, href, el);
    }
    const words = a.textContent ?? '';
    closeLink();
    emit();
    el.focus();
    placeCaretAfter(a);
    if (!pair || fromPair) return;
    if (applyToPair) {
      pair.onApply(href, words);
    } else if (!hasLinkTo(pair.value, href)) {
      showToast({
        status: 'warning',
        description: `The ${pair.label} version has no link to ${href} yet.`,
      });
    }
  }

  function removeLink() {
    if (editingLink) {
      editingLink.removeAttribute('data-editing');
      unwrapLink(editingLink);
    }
    closeLink();
    emit();
    root.current?.focus();
  }

  const keep = (e: ReactMouseEvent) => e.preventDefault();

  return (
    <div className="relative">
      {label && (
        <label id={labelId} className="mb-1 block text-xs font-medium text-gray-600">
          {label}
        </label>
      )}
      <div className="mb-1 flex flex-wrap items-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled}
          // mousedown, not click: keeps the editor's selection alive so openLink reads it.
          onMouseDown={keep}
          onClick={openLink}
          title="Select words in the text first, then press Link"
        >
          🔗 Link
        </Button>
        {linkOpen && (
          <div className="flex flex-1 items-end gap-2">
            <div className="min-w-0 flex-1">
              <Input
                label="Link URL"
                value={linkUrl}
                autoFocus
                placeholder="https://…"
                onChange={(e) => setLinkUrl((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    closeLink();
                  }
                }}
              />
            </div>
            <Button size="sm" onMouseDown={keep} onClick={applyLink}>
              Apply
            </Button>
            {editingLink && (
              <Button size="sm" variant="ghost" onMouseDown={keep} onClick={removeLink}>
                Remove link
              </Button>
            )}
            <Button size="sm" variant="ghost" onMouseDown={keep} onClick={closeLink}>
              Close
            </Button>
          </div>
        )}
      </div>
      {linkOpen && fromPair && (
        <p className="mb-1 text-xs text-blue-800">
          Select the words for this link in the {label || 'text'} below, then press Apply.
        </p>
      )}
      {linkOpen && pair && !fromPair && !editingLink && (
        <div className="mb-1" onMouseDown={keep}>
          <Checkbox
            label={`Apply this for the ${pair.label} version`}
            labelClassName="text-xs text-gray-600"
            checked={applyToPair}
            onCheckedChange={(v) => setApplyToPair(v === true)}
          />
        </div>
      )}
      <div className="relative">
        <div
          ref={root}
          id={fieldId}
          role="textbox"
          aria-multiline="true"
          aria-labelledby={label ? labelId : undefined}
          aria-disabled={disabled || undefined}
          contentEditable={disabled ? false : EDIT_MODE}
          suppressContentEditableWarning
          spellCheck
          onInput={onInput}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => window.setTimeout(() => setMenu(null), 150)}
          style={{ minHeight: `calc(${rows} * 1.5em + 1.25rem)` }}
          className={cn(
            'w-full whitespace-pre-wrap break-words rounded-2xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm leading-6 text-gray-800 shadow-sm transition',
            'focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100',
            disabled && 'cursor-not-allowed bg-gray-50'
          )}
        />
        {!value && placeholder && (
          <span className="pointer-events-none absolute left-3.5 top-2.5 text-sm text-gray-400">{placeholder}</span>
        )}
      </div>
      {menu && matches.length === 0 && menu.query && (
        <div className="absolute z-30 mt-1 w-80 rounded-2xl border border-gray-100 bg-white p-3 text-xs text-gray-500 shadow-xl">
          No variable matches “{menu.query}”. Delete the text after “/” to see them all.
        </div>
      )}
      {menu && matches.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-60 w-80 overflow-auto rounded-2xl border border-gray-100 bg-white p-1 shadow-xl">
          {matches.map((t, i) => (
            <button
              type="button"
              key={t.name}
              onMouseDown={(e) => {
                e.preventDefault();
                insertToken(t.name);
              }}
              onMouseEnter={() => setHi(i)}
              className={cn(
                'flex w-full items-start gap-2 rounded-xl px-2.5 py-2 text-left',
                i === hi ? 'bg-teal-50' : 'hover:bg-gray-50'
              )}
            >
              <span className="mt-0.5 shrink-0 rounded-full bg-teal-100 px-2 py-0.5 font-mono text-[11px] text-teal-800">
                {`{${t.name}}`}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-gray-800">{t.label}</span>
                <span className="block text-[11px] leading-snug text-gray-400">{t.desc}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 text-[11px] text-gray-400">
        Type <kbd className="rounded bg-gray-100 px-1 font-mono">/</kbd> to insert a variable
      </p>
    </div>
  );
}
